import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { FaPlus, FaNewspaper, FaSpinner, FaRobot, FaEye, FaEdit, FaPaperPlane } from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import ScreenHeader from "../../components/ScreenHeader";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { getAuthHeaders } from "../../utils/habiApi";
import ComunicacionesAdmin from "./comunicaciones/ComunicacionesAdmin";
import { colors, shadows, radius, spacing, typography, cardBase, btnPrimary, btnSecondary, btnGhost, inputBase, pageContainer, pageTitle, modalOverlay } from "../../utils/designTokens";

// URL del backend Habi para procesamiento con IA
const Habi_API_URL = (() => {
  const wsUrl = process.env.REACT_APP_PROPIA_WS_URL || "wss://propia-agent-241529496588.us-central1.run.app";
  return wsUrl.replace("wss://", "https://").replace("ws://", "http://").replace("/ws", "");
})();

const NovedadesEdificio = ({ setSelected }) => {
  const { buildings, selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [novedades, setNovedades] = useState([]);
  // Novedades (avisos que se leen en la app) | Comunicaciones (PDF por WhatsApp y email).
  const [pestana, setPestana] = useState("novedades");
  // Qué muestra la pestaña Comunicaciones: lista | editor {id} | estado {id}.
  const [vistaCom, setVistaCom] = useState({ tipo: "lista" });
  // false si el edificio no tiene Comunicaciones habilitadas: no se ofrece "Nueva comunicación".
  const [comDisponible, setComDisponible] = useState(false);

  // Estados para el modal de crear novedad
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transformingAI, setTransformingAI] = useState(false);
  const [showPreview, setShowPreview] = useState(false); // Vista previa antes de publicar
  const [successMsg, setSuccessMsg] = useState("");
  const [form, setForm] = useState({
    title: "",
    description: "",
  });

  // B2 — Modo "Generar desde tema": panel adicional con form estructurado
  // que pide tipo, tema, fecha, sector, urgencia y devuelve el comunicado
  // completo (no solo mejora un borrador).
  const [showGenerator, setShowGenerator] = useState(false);
  const [generatingCommunique, setGeneratingCommunique] = useState(false);
  const [genForm, setGenForm] = useState({
    type: "aviso",
    tema: "",
    event_date: "",
    time_range: "",
    sector: "",
    urgency: "info",
    details: "",
  });

  const handleGenerateCommunique = async () => {
    if (!genForm.tema.trim()) {
      showToast("Escribí el tema del comunicado.", "warning");
      return;
    }
    if (!selectedBuilding?.buildingid) {
      showToast("Seleccioná un edificio primero.", "warning");
      return;
    }
    try {
      setGeneratingCommunique(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(`${Habi_API_URL}/api/generate-communique`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(selectedBuilding.buildingid),
          type: genForm.type,
          tema: genForm.tema,
          event_date: genForm.event_date || null,
          time_range: genForm.time_range || null,
          sector: genForm.sector || null,
          urgency: genForm.urgency,
          details: genForm.details || null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo generar el comunicado.", "error");
        return;
      }
      const data = await resp.json();
      // Quitar tags HTML básicos del email_html para volcarlo a la descripción.
      const cleanHtml = (data.email_html || "")
        .replace(/<\/?(h1|h2|h3|p|strong|em|ul|li|div|br)[^>]*>/gi, (m) =>
          /^<\/(h1|h2|h3|p|div|ul|li)>$/i.test(m) ? "\n\n" :
          /^<br[^>]*>$/i.test(m) ? "\n" : ""
        )
        .replace(/<[^>]+>/g, "")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      setForm({
        title: data.title || genForm.tema,
        description: cleanHtml || data.whatsapp || genForm.tema,
      });
      setShowGenerator(false);
      showToast("Comunicado generado. Revisalo antes de publicar.", "success");
    } catch (e) {
      console.error("generate-communique error:", e);
      showToast(e.message || "Error inesperado al generar.", "error");
    } finally {
      setGeneratingCommunique(false);
    }
  };

  // Usar el filtro global del contexto
  const buildingsToProcess = selectedBuilding
    ? buildings.filter(b => b.buildingid === selectedBuilding.buildingid)
    : buildings;

  const fetchNovedades = async () => {
    if (!buildings || buildings.length === 0) return;

    try {
      let allNovedades = [];
      for (const b of buildingsToProcess) {
        const q = query(
          collection(db, "novedades"),
          where("buildingid", "==", Number(b.buildingid))
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data(), buildingName: b.name }));
        allNovedades = allNovedades.concat(data);
      }
      allNovedades.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
      setNovedades(allNovedades);
    } catch (error) {
      console.error("Error al traer novedades:", error);
    }
  };

  useEffect(() => {
    fetchNovedades();
  }, [selectedBuilding, buildings]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleOpenModal = () => {
    setForm({ title: "", description: "" });
    setShowPreview(false);
    setShowModal(true);
  };

  const handleSaveNovedad = async (asDraft = false) => {
    if (!selectedBuilding) {
      showToast("Selecciona un edificio primero", "warning");
      return;
    }
    if (!form.title.trim()) {
      showToast("El título es obligatorio", "warning");
      return;
    }
    if (!form.description.trim()) {
      showToast("La descripción es obligatoria", "warning");
      return;
    }

    // Si no es borrador y no estamos en preview, mostrar preview primero
    if (!asDraft && !showPreview) {
      setShowPreview(true);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        buildingid: Number(selectedBuilding.buildingid),
        title: form.title.trim(),
        description: form.description.trim(),
        date: serverTimestamp(),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
        active: !asDraft,
        published: !asDraft,
      };

      await addDoc(collection(db, "novedades"), payload);

      setShowModal(false);
      setShowPreview(false);
      setForm({ title: "", description: "" });
      fetchNovedades();
      setSuccessMsg(asDraft ? "Novedad guardada como borrador" : "Novedad publicada correctamente");
    } catch (error) {
      console.error("Error al guardar novedad:", error);
      showToast("Error al guardar la novedad: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishDraft = async (novedadId) => {
    try {
      const novedadRef = doc(db, "novedades", novedadId);
      await updateDoc(novedadRef, {
        published: true,
        active: true,
        publishedAt: serverTimestamp(),
        publishedBy: auth.currentUser?.email || "admin",
      });
      fetchNovedades();
      showToast("Novedad publicada exitosamente", "success");
    } catch (error) {
      console.error("Error al publicar novedad:", error);
      showToast("Error al publicar: " + error.message, "error");
    }
  };

  const handleTransformWithAI = async () => {
    if (!form.description.trim()) {
      showToast("Escribí primero el texto que querés mejorar", "warning");
      return;
    }

    setTransformingAI(true);
    try {
      const novedadAuthHeaders = await getAuthHeaders();
      const response = await fetch(`${Habi_API_URL}/api/transform-novedad`, {
        method: "POST",
        headers: novedadAuthHeaders,
        body: JSON.stringify({
          text: form.description,
          building_name: selectedBuilding?.name || "",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al transformar el texto");
      }

      const data = await response.json();
      if (data.success) {
        setForm({
          title: data.title || form.title,
          description: data.description || form.description
        });
      } else {
        throw new Error("No se obtuvo respuesta de la IA");
      }
    } catch (error) {
      console.error("Error transformando con IA:", error);
      showToast("Error al mejorar el texto: " + error.message, "error");
    } finally {
      setTransformingAI(false);
    }
  };

  const toDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div style={styles.page}>
      {/* Encabezado: título a la izquierda; pestañas y la acción de la pestaña a la derecha */}
      <div style={styles.pageHeader}>
        <ScreenHeader
          icon={FaNewspaper}
          title="Novedades"
          subtitle={pestana === "comunicaciones" ? "Comunicados en PDF por WhatsApp y email" : "Avisos que se leen en la app"}
        />
        <div style={styles.headerDerecha}>
          {selectedBuilding && (
            <div style={styles.tabs} role="tablist" aria-label="Novedades y comunicaciones">
              {[
                ["novedades", "Novedades"],
                ["comunicaciones", "Comunicaciones"],
              ].map(([valor, etiqueta]) => (
                <button
                  key={valor}
                  type="button"
                  role="tab"
                  aria-selected={pestana === valor}
                  style={{ ...styles.tab, ...(pestana === valor ? styles.tabActiva : {}) }}
                  onClick={() => setPestana(valor)}
                >
                  {etiqueta}
                </button>
              ))}
            </div>
          )}
          {pestana === "novedades" ? (
            <button
              style={{
                ...styles.addBtn,
                opacity: !selectedBuilding ? 0.5 : 1,
                cursor: !selectedBuilding ? 'not-allowed' : 'pointer'
              }}
              disabled={!selectedBuilding}
              onClick={handleOpenModal}
            >
              <FaPlus size={14} /> Crear Novedad
            </button>
          ) : (
            vistaCom.tipo === "lista" && comDisponible && (
              <button style={styles.addBtn} onClick={() => setVistaCom({ tipo: "editor" })}>
                <FaPlus size={14} /> Nueva comunicación
              </button>
            )
          )}
        </div>
      </div>

      {selectedBuilding && pestana === "comunicaciones" ? (
        <ComunicacionesAdmin
          buildingId={selectedBuilding.buildingid}
          edificioNombre={selectedBuilding.name || ""}
          showToast={showToast}
          vista={vistaCom}
          onVista={setVistaCom}
          onDisponible={setComDisponible}
        />
      ) : (
      <>

      {/* Mensaje de exito */}
      {successMsg && (
        <div style={{
          padding: "12px 16px",
          backgroundColor: colors.successLight,
          color: colors.successDark,
          borderRadius: radius.sm,
          marginBottom: spacing[4],
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <span>{successMsg}</span>
          <button onClick={() => setSuccessMsg("")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18 }} aria-label="Cerrar notificación">×</button>
        </div>
      )}

      {/* Aviso de seleccion de edificio */}
      {!selectedBuilding && (
        <div style={styles.selectBuildingNotice}>
          <p>Selecciona un edificio desde el filtro superior para ver y crear novedades.</p>
        </div>
      )}

      {/* Estado vacio o grilla de novedades */}
      {selectedBuilding && novedades.length === 0 ? (
        <div style={styles.emptyState}>
          <FaNewspaper size={40} color={colors.gray300} />
          <p style={styles.emptyText}>No hay novedades para este edificio.</p>
          <button style={styles.emptyBtn} onClick={handleOpenModal}>
            <FaPlus size={12} /> Crear primera novedad
          </button>
        </div>
      ) : selectedBuilding ? (
        <div style={styles.grid}>
          {novedades.map((novedad) => (
            <div key={novedad.id} style={{
              ...styles.card,
              borderLeft: novedad.published === false ? `4px solid ${colors.warning}` : `4px solid ${colors.primary}`,
              opacity: novedad.published === false ? 0.85 : 1,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={styles.cardDate}>{toDate(novedad.date)}</div>
                {novedad.published === false && (
                  <span style={{
                    fontSize: 11, fontWeight: 400, color: colors.warning,
                    background: colors.warningLight, padding: "2px 8px", borderRadius: 4,
                  }}>BORRADOR</span>
                )}
              </div>
              <h3 style={styles.cardTitle}>{novedad.title}</h3>
              <p style={styles.cardDescription}>{novedad.description}</p>
              {/* Boton para publicar borradores */}
              {novedad.published === false && (
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      padding: "6px 14px", background: colors.primary, color: colors.white,
                      border: "none", borderRadius: 6, cursor: "pointer",
                      fontWeight: 400, fontSize: 13,
                    }}
                    onClick={() => handlePublishDraft(novedad.id)}
                  >
                    <FaPaperPlane size={11} /> Publicar
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}
      </>
      )}

      {/* Modal crear novedad */}
      <AdminModal
        open={showModal}
        onClose={() => { setShowModal(false); setShowPreview(false); }}
        title={showPreview ? "Vista Previa" : "Nueva Novedad"}
        icon={<FaNewspaper />}
        size="medium"
        footer={
          showPreview ? (
            <>
              <button
                style={styles.cancelBtn}
                className="habitia-btn-secondary"
                onClick={() => setShowPreview(false)}
                disabled={saving}
              >
                <FaEdit size={12} style={{ marginRight: 6 }} />
                Editar
              </button>
              <button
                style={{ ...styles.cancelBtn, borderColor: colors.warning, color: colors.warningDark }}
                onClick={() => handleSaveNovedad(true)}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar Borrador"}
              </button>
              <button
                style={styles.saveBtn}
                className="habitia-btn-primary"
                onClick={() => handleSaveNovedad(false)}
                disabled={saving}
              >
                {saving ? "Publicando..." : "Publicar"}
              </button>
            </>
          ) : (
            <>
              <button
                style={styles.cancelBtn}
                className="habitia-btn-secondary"
                onClick={() => { setShowModal(false); setShowPreview(false); }}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                style={{ ...styles.cancelBtn, borderColor: colors.warning, color: colors.warningDark }}
                onClick={() => handleSaveNovedad(true)}
                disabled={saving || !form.title.trim() || !form.description.trim()}
              >
                Guardar Borrador
              </button>
              <button
                style={{
                  ...styles.saveBtn,
                  opacity: !form.title.trim() || !form.description.trim() ? 0.5 : 1,
                }}
                className="habitia-btn-primary"
                onClick={() => handleSaveNovedad(false)}
                disabled={saving || !form.title.trim() || !form.description.trim()}
              >
                <FaEye size={12} style={{ marginRight: 6 }} />
                Previsualizar
              </button>
            </>
          )
        }
      >
              <div style={styles.buildingInfo}>
                <span style={styles.buildingLabel}>Edificio:</span>
                <span style={styles.buildingName}>{selectedBuilding?.name}</span>
              </div>

              {showPreview ? (
                /* Vista previa de la novedad antes de publicar */
                <div>
                  <div style={{
                    padding: 16, background: colors.gray50, borderRadius: 10,
                    border: `1px solid ${colors.gray200}`, borderLeft: `4px solid ${colors.primary}`,
                    marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 6, fontWeight: 400 }}>
                      Vista previa — asi se vera para los residentes
                    </div>
                    <h3 style={{ fontSize: 16, fontWeight: 400, color: colors.black, margin: "0 0 8px" }}>
                      {form.title}
                    </h3>
                    <p style={{ fontSize: 14, color: colors.gray600, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>
                      {form.description}
                    </p>
                  </div>
                  <div style={{
                    padding: "10px 14px", background: colors.warningLight, borderRadius: radius.sm,
                    borderLeft: `3px solid ${colors.warning}`, fontSize: 13, color: colors.warningDark,
                  }}>
                    Revisa el contenido antes de publicar. Una vez publicado sera visible para todos los residentes.
                  </div>
                </div>
              ) : (
                /* Formulario de edicion */
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Titulo *</label>
                    <input
                      type="text"
                      style={styles.input}
                      placeholder="Ej: Corte de agua programado"
                      value={form.title}
                      onChange={e => setForm({ ...form, title: e.target.value })}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Descripcion *</label>
                    <textarea
                      style={styles.textarea}
                      placeholder="Escribe tu mensaje de forma informal (ej: 'Manana cortan el agua de 9 a 12 por arreglo de canos')..."
                      value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      rows={5}
                      disabled={transformingAI}
                    />
                  </div>

                  {/* Seccion de IA */}
                  <div style={styles.aiSection}>
                    <div style={styles.aiSectionHeader}>
                      <div style={styles.aiSectionIcon}>
                        <FaRobot size={18} color={colors.primary} />
                      </div>
                      <div>
                        <h4 style={styles.aiSectionTitle}>Asistente IA</h4>
                        <p style={styles.aiSectionSubtitle}>Mejorá tu borrador o generá uno desde cero a partir de un tema.</p>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={{
                          ...styles.aiProcessBtn,
                          opacity: transformingAI || !form.description.trim() ? 0.5 : 1,
                          cursor: transformingAI || !form.description.trim() ? 'not-allowed' : 'pointer',
                          flex: '1 1 220px',
                          justifyContent: 'center',
                          padding: '10px 16px',
                        }}
                        onClick={handleTransformWithAI}
                        disabled={transformingAI || !form.description.trim()}
                      >
                        {transformingAI ? (
                          <>
                            <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                            Procesando…
                          </>
                        ) : (
                          <>
                            <FaRobot size={14} />
                            Mejorar borrador
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '10px 16px',
                          fontSize: 13,
                          color: colors.primary,
                          background: colors.white,
                          border: `1px solid ${colors.primaryLight}`,
                          borderRadius: 8,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          flex: '1 1 220px',
                          justifyContent: 'center',
                        }}
                        onClick={() => setShowGenerator(true)}
                      >
                        ✨ Generar desde un tema
                      </button>
                    </div>
                  </div>
                </>
              )}
      </AdminModal>

      {/* B2 — Modal del generador desde tema */}
      {showGenerator && (
        <div style={modalOverlay}>
          <div style={{
            background: colors.white,
            borderRadius: radius.lg,
            padding: spacing[6],
            maxWidth: 600,
            width: "90%",
            maxHeight: "85vh",
            overflowY: "auto",
            boxShadow: shadows.modal,
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: spacing[4],
            }}>
              <h3 style={{ margin: 0, fontSize: typography.sizes.lg, color: colors.primary, fontWeight: 400 }}>
                ✨ Generar comunicado desde tema
              </h3>
              <button
                onClick={() => setShowGenerator(false)}
                style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer", color: colors.gray600 }}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={styles.label}>Tipo *</label>
                <select
                  style={styles.input}
                  value={genForm.type}
                  onChange={(e) => setGenForm({ ...genForm, type: e.target.value })}
                >
                  <option value="aviso">Aviso</option>
                  <option value="mantenimiento">Mantenimiento</option>
                  <option value="asamblea">Asamblea</option>
                  <option value="cobranza">Cobranza</option>
                  <option value="bienvenida">Bienvenida</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label style={styles.label}>Tema *</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Ej: Corte de agua programado por reparación de cañería"
                  value={genForm.tema}
                  onChange={(e) => setGenForm({ ...genForm, tema: e.target.value })}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={styles.label}>Fecha del evento</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={genForm.event_date}
                    onChange={(e) => setGenForm({ ...genForm, event_date: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Horario</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="Ej: 9-12hs"
                    value={genForm.time_range}
                    onChange={(e) => setGenForm({ ...genForm, time_range: e.target.value })}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={styles.label}>Sector afectado</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="Ej: Sector A, Piso 3, todo el edificio"
                    value={genForm.sector}
                    onChange={(e) => setGenForm({ ...genForm, sector: e.target.value })}
                  />
                </div>
                <div>
                  <label style={styles.label}>Urgencia</label>
                  <select
                    style={styles.input}
                    value={genForm.urgency}
                    onChange={(e) => setGenForm({ ...genForm, urgency: e.target.value })}
                  >
                    <option value="info">Info</option>
                    <option value="importante">Importante</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={styles.label}>Detalles adicionales (opcional)</label>
                <textarea
                  style={{ ...styles.input, minHeight: 70, resize: "vertical" }}
                  placeholder="Cualquier dato extra que la IA deba incluir."
                  value={genForm.details}
                  onChange={(e) => setGenForm({ ...genForm, details: e.target.value })}
                />
              </div>
            </div>

            <div style={{
              display: "flex",
              gap: 12,
              marginTop: spacing[4],
              justifyContent: "flex-end",
              flexWrap: "wrap",
            }}>
              <button
                onClick={() => setShowGenerator(false)}
                style={btnGhost}
                disabled={generatingCommunique}
              >
                Cancelar
              </button>
              <button
                onClick={handleGenerateCommunique}
                style={btnPrimary}
                disabled={generatingCommunique || !genForm.tema.trim()}
              >
                {generatingCommunique ? "Generando…" : "Generar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Estilos con design tokens ───────────────────────────────────────────────
const styles = {
  page: {
    ...pageContainer,
  },
  headerDerecha: { display: "flex", alignItems: "center", gap: spacing[3], flexWrap: "wrap", justifyContent: "flex-end" },
  // Pestañas compactas (segmentadas) en el encabezado, para no ocupar una fila.
  tabs: { display: "inline-flex", gap: 2, padding: 3, background: colors.gray100, borderRadius: radius.sm },
  tab: {
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: radius.sm - 2,
    padding: "7px 14px",
    fontSize: typography.sizes.base,
    fontFamily: typography.fontFamily,
    fontWeight: 400,
    color: colors.gray600,
    cursor: "pointer",
  },
  tabActiva: { background: colors.white, color: colors.primaryDark, border: `1px solid ${colors.gray200}`, boxShadow: shadows.sm },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[3],
    flexWrap: "wrap",
    marginBottom: spacing[4],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    ...pageTitle,
  },
  pageSubtitle: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
  },
  filterBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: colors.white,
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    padding: "8px 12px",
  },
  filterSelect: {
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
    cursor: "pointer",
  },
  addBtn: {
    ...btnPrimary,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    gap: 12,
  },
  emptySubtext: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
    textAlign: "center",
  },
  emptyText: {
    color: colors.gray600,
    fontSize: typography.sizes.md,
    margin: 0,
  },
  buildingSelect: {
    padding: "12px 16px",
    border: `1px solid ${colors.gray200}`,
    borderRadius: radius.sm,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    background: colors.gray50,
    color: colors.gray900,
    minWidth: 280,
    cursor: "pointer",
    marginTop: 8,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: spacing[4],
  },
  card: {
    ...cardBase,
    borderLeft: `4px solid ${colors.primary}`,
  },
  cardDate: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    marginBottom: 8,
    fontWeight: 400,
  },
  cardTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
    margin: "0 0 8px",
  },
  cardDescription: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    lineHeight: 1.5,
    margin: 0,
  },
  selectBuildingNotice: {
    background: colors.white,
    boxShadow: shadows.sm,
    borderRadius: radius.md,
    padding: 40,
    textAlign: "center",
    color: colors.gray600,
    fontSize: typography.sizes.md,
  },
  emptyBtn: {
    ...btnPrimary,
    marginTop: 8,
  },
  buildingInfo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 20,
    padding: "12px 16px",
    background: colors.primarySubtle,
    borderRadius: radius.sm,
  },
  buildingLabel: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
  },
  buildingName: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.primary,
  },
  formGroup: {
    marginBottom: spacing[4],
  },
  label: {
    display: "block",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
    marginBottom: 6,
  },
  input: {
    ...inputBase,
    boxSizing: "border-box",
  },
  textarea: {
    ...inputBase,
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
  aiSection: {
    marginBottom: spacing[4],
    padding: spacing[4],
    background: `linear-gradient(135deg, rgba(0,128,255,0.05) 0%, rgba(0,194,255,0.05) 100%)`,
    borderRadius: radius.md,
    border: `1px solid rgba(0,128,255,0.15)`,
  },
  aiSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    marginBottom: spacing[3],
  },
  aiSectionIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(0,128,255,0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  aiSectionTitle: {
    margin: 0,
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.black,
  },
  aiSectionSubtitle: {
    margin: "2px 0 0 0",
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  aiProcessBtn: {
    ...btnPrimary,
  },
  infoNote: {
    padding: "12px 16px",
    background: colors.gray50,
    borderRadius: radius.sm,
    borderLeft: `3px solid ${colors.primary}`,
  },
  cancelBtn: {
    ...btnSecondary,
  },
  saveBtn: {
    ...btnPrimary,
  },
};

export default NovedadesEdificio;
